import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2, Search, X, Copy, ClipboardPaste, CalendarPlus } from 'lucide-react'
import { Input, type BulkAction, Modal, Button } from '@atrvd/saas-ui'
import { ProjectionFilters } from '@/components/banking/ProjectionFilters'
import { ProjectionsTable } from '@/components/banking/ProjectionsTable'
import { ProjectionFormDialog } from '@/components/banking/ProjectionFormDialog'
import { ProjectionBatchFormDialog } from '@/components/banking/ProjectionBatchFormDialog'
import { useProjections, useDeleteProjection, useCreateProjections, useUpdateProjection, useCategories, useFindProjectionsPage } from '@/hooks/useProjections'
import { useFacilities } from '@/hooks/useBanking'
import { useTablePagination } from '@/hooks/useTablePagination'
import { tableQueryState } from '@/components/common/queryErrorDisplay'
import type { ProjectionFilters as ProjectionFiltersType, ProjectionFormData } from '@/types/projections'
import type { ProjectionWithRelations } from '@/lib/supabase'
import { getProjectionAmountInfo } from '@/lib/projection-utils'
import dayjs from '@dcp/shared/utils/dayjs'
import { nextCalendarDay } from '@/lib/date-utils'
import { toast } from '@atrvd/saas-ui'

/** Stable empty array to avoid re-renders while the projections query has no data */
const EMPTY_PROJECTIONS: ProjectionWithRelations[] = []

/** Duration in ms before row highlight animation is cleared */
const HIGHLIGHT_DURATION_MS = 5000

/**
 * Parse inline description edit into update data.
 * Returns null if value unchanged (no-op).
 */
function parseDescriptionEdit(
  projection: ProjectionWithRelations,
  value: unknown,
): Partial<ProjectionFormData> | null {
  // Use null (not undefined) so db.update sends NULL to clear the field
  const normalizeDesc = (val: string | null | undefined): string | null => val?.trim() || null
  const newDesc = normalizeDesc(String(value ?? ''))
  if (normalizeDesc(projection.description) === newDesc) return null
  return { description: newDesc }
}

/**
 * Parse inline amount edit into update data.
 * Returns null if value unchanged (no-op).
 */
function parseAmountEdit(
  projection: ProjectionWithRelations,
  value: unknown,
): Partial<ProjectionFormData> | null {
  if (value === '' || value === null || value === undefined) {
    // Clearing amount is valid for draft projections — sets the signed amount to 0
    return { amount: 0 }
  }

  const num = Number(value)
  if (isNaN(num)) {
    // editConfig.validate already blocks NaN; reaching here indicates a code bug
    if (import.meta.env.DEV) console.error('BUG: NaN reached parseAmountEdit despite validation')
    return null
  }

  // DCP-250: the typed number IS the signed value (negatives = outflow).
  // No-op check: skip if the signed amount hasn't changed.
  if (num === (projection.amount ?? 0)) return null

  return { amount: num }
}

/**
 * Daily Cashflow Projections page
 *
 * Displays and manages cash flow projections with:
 * - Date range, type, facility, and category filters
 * - Table showing all projections with related data
 * - Create, edit, and delete operations via dialog
 */
export function ProjectionsPage() {
  // State management
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<ProjectionFiltersType>({})
  // The total comes back with the page (`{ items, total }`), so it goes
  // through `forTotal` after the page query. A filter/search change (a new
  // `filters` value) goes back to page 1.
  const pagination = useTablePagination({
    totalItems: undefined,
    resetKey: JSON.stringify(filters),
  })
  const { page, pageSize, setPage } = pagination

  // Initialise search input from URL param on mount
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '')

  // Last search term the debounce below applied to `filters` (starts as the
  // initial `filters.search`, i.e. none).
  const appliedSearchRef = useRef<string | undefined>(undefined)

  // Debounce search input — update filters.search and URL after 300ms of inactivity
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim() || undefined
      // The effect also re-runs with an UNCHANGED search: react-router's
      // `setSearchParams` gets a new identity whenever the URL changes, which
      // this very timer causes. Without this guard that re-run reset the page
      // ~300ms later, throwing the user back to page 1 — e.g. right after a
      // create had opened the page of the new rows (DCP-406).
      if (appliedSearchRef.current === trimmed) return
      appliedSearchRef.current = trimmed
      // A changed search changes `filters`, which takes the table back to page 1.
      setFilters((prev) => {
        if (prev.search === trimmed) return prev
        return { ...prev, search: trimmed }
      })
      // Sync search term to URL (replace to avoid history pollution)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (trimmed) {
          next.set('search', trimmed)
        } else {
          next.delete('search')
        }
        return next
      }, { replace: true })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, setSearchParams])
  const [isBatchFormOpen, setIsBatchFormOpen] = useState(false)
  const [isEditFormOpen, setIsEditFormOpen] = useState(false)
  const [editingProjection, setEditingProjection] = useState<ProjectionWithRelations | undefined>()
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set())
  // Ref to track the active highlight-clear timer so we can cancel it if needed
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout>>()

  // Row selection state for multiselect
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set())

  // Clear selection when page, page size or filters change so stale selections don't linger
  useEffect(() => {
    setSelectedRows(new Set())
  }, [page, pageSize, filters])

  // ID of projection pending delete confirmation, null means dialog closed
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // App-level clipboard: stores copied projection data until next copy or page leave
  const [clipboard, setClipboard] = useState<ProjectionWithRelations[]>([])

  // Data fetching with pagination
  const projectionsQuery = useProjections({
    ...filters,
    page,
    pageSize,
  })
  const projections = projectionsQuery.data?.items ?? EMPTY_PROJECTIONS
  const projectionsPlaceholder = projectionsQuery.isPlaceholderData

  // A page past the end (the last row of the last page deleted, a smaller
  // result after a refetch) moves to the new last page — but only on a settled
  // answer: right after a create opens the page of the new rows, the older
  // total still on screen may not reach that page yet.
  const { dataTablePagination } = pagination.forTotal({
    total: projectionsQuery.data?.total,
    isFetching: projectionsQuery.isFetching,
  })

  const { data: categoriesData } = useCategories()
  const categories = categoriesData ?? []
  const { data: facilities = [] } = useFacilities()
  const deleteProjection = useDeleteProjection()
  const createProjections = useCreateProjections()
  const updateProjection = useUpdateProjection()
  const findProjectionsPage = useFindProjectionsPage()

  // Ref for filters: handleProjectionsCreated deliberately leaves `filters`
  // out of its deps (it would be recreated on every filter change), and a
  // dialog calls `onCreated` from the closure of its submit handler, which
  // started before the create finished. Reading the ref gives the filters in
  // effect when the lookup starts, and lets the answer be checked against the
  // filters in effect when it arrives.
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  // Ref for the current page, for the same reason: the lookup's answer is
  // applied only if the user has not turned the page while it was in flight.
  const pageRef = useRef(page)
  pageRef.current = page
  // The page size the lookup must use, read at the same moment as the page.
  const pageSizeRef = useRef(pageSize)
  pageSizeRef.current = pageSize
  // Sequence number of the latest create's page lookup. A lookup that resolves
  // after a newer one started (or after unmount) is discarded, so a slow
  // answer can never move the table away from the most recent create.
  const createdLookupRef = useRef(0)

  // Ref for projections so handleInlineSave always reads fresh data without
  // recreating the callback on every data refetch (pagination, filter, mutation).
  const projectionsRef = useRef(projections)
  projectionsRef.current = projections

  /**
   * Handle inline cell edit save.
   * Maps 'description' and 'amount' column keys to the correct ProjectionFormData fields.
   * For amount: preserves the credit/debit direction based on current values.
   */
  const handleInlineSave = useCallback(async (
    rowKey: string | number,
    columnKey: string,
    value: unknown,
  ) => {
    if (updateProjection.isPending) return
    const id = String(rowKey)
    const projection = projectionsRef.current.find((p) => p.id === id)
    if (!projection) {
      toast.error('Projection not found. It may have been deleted.')
      return
    }

    let data: Partial<ProjectionFormData> | null = null

    if (columnKey === 'description') {
      data = parseDescriptionEdit(projection, value)
    } else if (columnKey === 'amount') {
      data = parseAmountEdit(projection, value)
    } else {
      if (import.meta.env.DEV) console.warn(`Inline edit for column '${columnKey}' is not implemented`)
      return
    }

    if (!data) return // No-op: value unchanged

    try {
      await updateProjection.mutateAsync({ id, data })
      toast.success('Updated')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update'
      toast.error(message)
    }
  }, [updateProjection])

  // Clean up highlight timer on unmount to prevent setState on unmounted component
  useEffect(() => {
    const lookups = createdLookupRef
    return () => {
      // Discard a page lookup still in flight — it must not start a timer
      // after the cleanup below has run.
      lookups.current += 1
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current)
      }
    }
  }, [])

  /**
   * Open the page that shows the newly created rows and highlight them.
   *
   * Since DCP-406 the table sorts by Projected Payment Date, so a new row
   * lands where its date puts it — often not on page 1. The page holding the
   * FIRST of the new rows (in table order) is looked up under the current
   * filters and search. When none of them is visible under those filters, or
   * the lookup fails, the table falls back to page 1 silently: the create
   * itself succeeded and was already confirmed, a missing highlight is not
   * worth an error toast.
   *
   * The highlight lasts 5 seconds and starts once the page is known, so the
   * window is spent on the page the user actually sees. An in-flight timer is
   * cancelled, so rapid back-to-back creates restart the window.
   *
   * Only the latest create's answer is used (an older one resolving late is
   * dropped). An answer that arrives after the user changed the filters or
   * turned the page does not move the page — the user's choice wins — but the
   * rows are still highlighted.
   *
   * @param ids - ids of the projections just created
   */
  const handleProjectionsCreated = useCallback((ids: string[]) => {
    createdLookupRef.current += 1
    const lookup = createdLookupRef.current
    const filtersAtStart = filtersRef.current
    const pageAtStart = pageRef.current
    const pageSizeAtStart = pageSizeRef.current
    void findProjectionsPage(ids, filtersAtStart, pageSizeAtStart)
      .catch((error: unknown) => {
        if (import.meta.env.DEV) console.warn('Could not locate the page of the new projections', error)
        return null
      })
      .then((foundPage) => {
        if (lookup !== createdLookupRef.current) return
        // The user acted while the lookup was in flight — keep their page,
        // only highlight:
        //   - filters (search included) changed: the page was computed for
        //     the old scope, and the change already reset the table to page 1.
        //     `filters` changes identity only on a real change (the debounce
        //     returns the previous object for an unchanged search);
        //   - the page was turned by hand: overriding it would yank the table
        //     away from where the user just went;
        //   - the page size changed: the answer counts pages of the old size.
        if (
          filtersRef.current === filtersAtStart &&
          pageRef.current === pageAtStart &&
          pageSizeRef.current === pageSizeAtStart
        ) {
          setPage(foundPage ?? 1)
        }
        setHighlightedIds(new Set(ids))
        if (highlightTimerRef.current) {
          clearTimeout(highlightTimerRef.current)
        }
        highlightTimerRef.current = setTimeout(() => {
          setHighlightedIds(new Set())
        }, HIGHLIGHT_DURATION_MS)
      })
  }, [findProjectionsPage, setPage])

  /**
   * Copy selected projections to the app-level clipboard.
   * Resolves the full projection objects from selectedRows IDs.
   */
  const handleCopyProjections = useCallback(() => {
    const selected = projections.filter((p) => selectedRows.has(p.id))
    if (selected.length === 0) return
    setClipboard(selected)
    toast.success(`${selected.length} projection${selected.length > 1 ? 's' : ''} copied`)
  }, [projections, selectedRows])

  /**
   * Paste projections from clipboard — creates new records with today's date.
   * Copies facility, category, amount, description, priority, include_in_report.
   */
  const handlePasteProjections = useCallback(async () => {
    if (clipboard.length === 0 || createProjections.isPending) return

    // Business decision: use the user's local date for pasted projections.
    // This ensures "today" matches what the user sees on their calendar/clock.
    // Trade-off: users in different timezones may get different dates for the same
    // UTC moment, but for financial projections the user's local date is correct.
    const today = dayjs().format('YYYY-MM-DD')

    const payloads: ProjectionFormData[] = clipboard.map((p) => {
      // DCP-250: copy the single signed amount verbatim (negatives = outflow).
      const { amount } = getProjectionAmountInfo(p)
      return {
        category_id: p.category_id,
        facility_id: p.facility_id ?? undefined,
        date: today,
        description: p.description?.trim() || null,
        amount,
        notes: p.notes ?? undefined,
        priority: p.priority ?? false,
        include_in_report: p.include_in_report ?? true,
      }
    })

    try {
      const result = await createProjections.mutateAsync(payloads)
      const newIds = result?.map((r) => r.id) ?? []
      handleProjectionsCreated(newIds)
      setSelectedRows(new Set())
      setClipboard([])
      toast.success(`${payloads.length} projection${payloads.length > 1 ? 's' : ''} pasted`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to paste projections'
      toast.error(message)
    }
  }, [clipboard, createProjections, handleProjectionsCreated])

  const handleMoveToNextDay = useCallback(async (projection: ProjectionWithRelations) => {
    if (updateProjection.isPending) return
    try {
      await updateProjection.mutateAsync({
        id: projection.id,
        data: { date: nextCalendarDay(projection.date) },
      })
      toast.success('Projected Payment Date updated successfully')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update Projected Payment Date'
      toast.error(message)
    }
  }, [updateProjection])

  const handleBulkMoveToNextDay = useCallback(async () => {
    const selected = projections.filter((p) => selectedRows.has(p.id))
    if (selected.length === 0 || updateProjection.isPending) return

    const results = await Promise.allSettled(
      selected.map((p) =>
        updateProjection.mutateAsync({ id: p.id, data: { date: nextCalendarDay(p.date) } }),
      ),
    )

    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    const failed = results.length - succeeded

    setSelectedRows(new Set())

    if (failed === 0) {
      toast.success(`Projected Payment Date updated for ${succeeded} projection${succeeded > 1 ? 's' : ''}`)
    } else if (succeeded === 0) {
      toast.error(`Failed to update Projected Payment Date for ${failed} projection${failed > 1 ? 's' : ''}`)
    } else {
      toast.error(`Updated ${succeeded}, failed ${failed}. Please retry the failed projections.`)
    }
  }, [projections, selectedRows, updateProjection])

  // Refs for stable keyboard handler — avoid re-subscribing on every state change
  const copyRef = useRef(handleCopyProjections)
  copyRef.current = handleCopyProjections
  const pasteRef = useRef(handlePasteProjections)
  pasteRef.current = handlePasteProjections
  const selectedRowsRef = useRef(selectedRows)
  selectedRowsRef.current = selectedRows
  const clipboardRef = useRef(clipboard)
  clipboardRef.current = clipboard
  const moveNextDayRef = useRef(handleBulkMoveToNextDay)
  moveNextDayRef.current = handleBulkMoveToNextDay

  /**
   * Keyboard shortcuts: Cmd/Ctrl+C to copy, Cmd/Ctrl+V to paste.
   * Only active when focus is NOT inside an input/textarea to avoid conflicts.
   * Uses refs so the effect never re-subscribes when state changes.
   */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.target instanceof HTMLElement)) return
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return
      }

      const isMod = e.metaKey || e.ctrlKey

      if (isMod && e.key === 'c' && selectedRowsRef.current.size > 0) {
        e.preventDefault()
        copyRef.current()
      }

      if (isMod && e.key === 'v' && clipboardRef.current.length > 0) {
        e.preventDefault()
        pasteRef.current()
      }
    }

    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  /**
   * Bulk actions for the DataTable toolbar.
   * Copy is always visible when rows are selected; Paste appears when clipboard has data.
   */
  const bulkActions: BulkAction<ProjectionWithRelations>[] = useMemo(() => [
    {
      id: 'copy',
      label: 'Copy',
      icon: <Copy className="h-4 w-4" />,
      onAction: () => copyRef.current(),
    },
    {
      id: 'paste',
      label: `Paste (${clipboard.length})`,
      icon: <ClipboardPaste className="h-4 w-4" />,
      onAction: () => pasteRef.current(),
      showWhen: () => clipboardRef.current.length > 0,
    },
    {
      id: 'move-next-day',
      label: 'Move to next day',
      icon: <CalendarPlus className="h-4 w-4" />,
      onAction: () => moveNextDayRef.current(),
    },
  ], [clipboard.length])

  /** Memoized selection config to avoid re-creating on every render */
  const handleSelectionChange = useCallback((rows: Set<string | number>) => {
    setSelectedRows(rows)
  }, [])

  // The previous page stays on screen while the next loads. A row ticked there
  // would vanish when the page lands yet stay selected (the clear above has
  // already run), and Copy/Move would act on rows no longer shown.
  const selection = useMemo(() => ({
    selectedRows,
    onSelectionChange: handleSelectionChange,
    mode: 'multiple' as const,
    // Placeholder rows must not be selectable: they may be replaced when the
    // page lands, and Copy/Move would write a row that is no longer shown.
    isRowSelectable: () => !projectionsPlaceholder,
  }), [selectedRows, handleSelectionChange, projectionsPlaceholder])

  /** Memoized editing config — handleInlineSave is already stable via ref pattern */
  const editing = useMemo(() => ({
    onSave: handleInlineSave,
  }), [handleInlineSave])

  /**
   * Handle opening create dialog (batch form)
   */
  const handleCreate = () => {
    setIsBatchFormOpen(true)
  }

  /**
   * Handle opening edit dialog with selected projection
   */
  const handleEdit = useCallback((projection: ProjectionWithRelations) => {
    setEditingProjection(projection)
    setIsEditFormOpen(true)
  }, [])

  /**
   * Open delete confirmation dialog
   */
  const handleDelete = useCallback((projectionId: string) => {
    setDeleteConfirmId(projectionId)
  }, [])

  /**
   * Execute deletion after user confirms
   */
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId || deleteProjection.isPending) return

    try {
      await deleteProjection.mutateAsync(deleteConfirmId)
      toast.success('Projection deleted')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete projection'
      toast.error(message)
    } finally {
      setDeleteConfirmId(null)
    }
  }

  /**
   * Handle clearing all filters
   */
  const handleClearFilters = () => {
    setFilters({})
    setSearchInput('')
    setSearchParams({}, { replace: true })
  }

  /**
   * Handle closing edit form dialog
   */
  const handleEditFormClose = () => {
    setIsEditFormOpen(false)
    setEditingProjection(undefined)
  }

  // Resolve projection details for delete confirmation dialog
  const deleteModalDesc = useMemo(() => {
    if (!deleteConfirmId) return 'Are you sure you want to delete this projection? This action cannot be undone.'
    const proj = projections.find((p) => p.id === deleteConfirmId)
    return proj
      ? `Delete "${proj.description || 'Untitled'}" (${proj.date})? This action cannot be undone.`
      : 'Are you sure you want to delete this projection? This action cannot be undone.'
  }, [deleteConfirmId, projections])

  return (
    <div className="space-y-6">
      {/* Page Header with Search */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Daily Cashflow Projections</h1>
          <p className="text-sm text-muted-foreground">
            Manage and track your cash flow projections
          </p>
        </div>
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search projections..."
            className="pl-9 pr-8"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filters with Add Projection button */}
      <ProjectionFilters
        filters={filters}
        onFiltersChange={setFilters}
        onAddProjection={handleCreate}
        onClearFilters={handleClearFilters}
        facilityOptions={(facilities ?? []).map((f) => ({ value: f.id, label: f.name }))}
        categoryOptions={(categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
      />

      {/* Projections Table */}
      <ProjectionsTable
        projections={projections}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMoveToNextDay={handleMoveToNextDay}
        onClearFilters={handleClearFilters}
        pagination={dataTablePagination}
        highlightedIds={highlightedIds}
        searchTerm={filters.search}
        selection={selection}
        bulkActions={bulkActions}
        editing={editing}
        {...tableQueryState(projectionsQuery, projections.length)}
      />

      {/* Batch Create Dialog */}
      <ProjectionBatchFormDialog
        open={isBatchFormOpen}
        onOpenChange={setIsBatchFormOpen}
        onCreated={handleProjectionsCreated}
      />

      {/* Edit Dialog */}
      <ProjectionFormDialog
        open={isEditFormOpen}
        onOpenChange={handleEditFormClose}
        projection={editingProjection}
        onCreated={handleProjectionsCreated}
      />

      {/* Delete Confirmation Dialog */}
      <Modal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Projection"
        description={deleteModalDesc}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleteProjection.isPending}>
              {deleteProjection.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : 'Delete'}
            </Button>
          </div>
        }
      >
        {/* Content lives in description prop; empty fragment satisfies ModalProps.children */}
        <></>
      </Modal>
    </div>
  )
}
